/**
 * Which partner connections a vendor is allowed to see.
 *
 * The integrations module lists what a processor may use. QuickBooks and
 * Stripe are one entry each; partners used to be a single `partners` entry
 * covering all of them, which was fine with one adapter and wrong with two —
 * a plant running PPS should not be shown a VistaTrac tab they have no server
 * for.
 *
 * So a partner is now listed individually, as `partner:<adapter key>`. The keys
 * come from the adapter registry rather than a list kept here, which is the
 * same bargain the rest of the harness makes: adding partner #3 is an adapter
 * file and a registry line, not an edit to every surface that mentions
 * partners.
 */

/** Marks an entry as naming one adapter rather than a first-party integration. */
export const PARTNER_PREFIX = 'partner:';

/**
 * What the bare `partners` entry covers, frozen.
 *
 * Before partners were listed individually, one `partners` entry meant "all of
 * them" — which was fine when adding an adapter was a rare event. Left as
 * literally all of them, it becomes a standing grant: every integration we
 * ever ship would switch itself on for these vendors the day it is registered,
 * without anyone deciding to.
 *
 * So the legacy entry covers exactly the adapters that existed when it was the
 * only mechanism, and nothing after. A vendor keeps everything they had;
 * anything new needs its own `partner:<key>` entry, like every other vendor.
 *
 * **Do not add to this list.** A new adapter belongs on the vendors an admin
 * ticks it for.
 */
const LEGACY_PARTNERS_COVERS = ['reference_http', 'pps', 'vistatrac'];

/**
 * The module entry that allows one adapter.
 * @param adapter - Registry key, e.g. `vistatrac`.
 * @returns The entry to store.
 */
export function partnerEntryFor(adapter: string): string {
  return `${PARTNER_PREFIX}${adapter}`;
}

/**
 * Whether this vendor may use one partner.
 * @param allowed - The module's `allowedIntegrations`.
 * @param adapter - Registry key.
 * @returns True when the adapter is allowed.
 */
export function isPartnerAllowed(
  allowed: readonly string[],
  adapter: string,
): boolean {
  if (allowed.includes(partnerEntryFor(adapter))) return true;
  // The legacy blanket entry, honoured rather than migrated: it needs no
  // script, and a vendor set up before per-adapter toggles keeps exactly what
  // they had. Scoped to the adapters it actually covered, so it does not
  // quietly grant the next one — see LEGACY_PARTNERS_COVERS.
  return (
    allowed.includes('partners') && LEGACY_PARTNERS_COVERS.includes(adapter)
  );
}

/**
 * Whether the partner section should appear at all.
 *
 * Deliberately does not consult the registry: a vendor allowed an adapter this
 * deployment no longer registers should still see the section, with that
 * connection absent, rather than have the whole section vanish and take their
 * other partners with it.
 * @param allowed - The module's `allowedIntegrations`.
 * @returns True when any partner is allowed.
 */
export function anyPartnerAllowed(allowed: readonly string[]): boolean {
  return allowed.some(
    (entry) => entry === 'partners' || entry.startsWith(PARTNER_PREFIX),
  );
}
